// Ultimate Card Game (UCG) — AI 1-Step State Projector
//
// Computes 1-step lookahead state projections, checks context tags in O(1),
// and evaluates multi-card hand combos and virtual trigger nodes.
// Referenced in docs/AI_COMBINATORICS_AND_TEST_SUITE_PLAN_2026-10-28.md (Section 2.1).

import { AI_SYNERGY_PAIRS } from '../../data/aiSynergyMatrix.js';
import { getCardByName } from '../../data/cards.js';

/**
 * Checks in O(1) whether the tactical precondition of a synergy pair is met.
 *
 * @param {string} contextTag
 * @param {object} bot - BotAI instance
 * @param {object} view - BoardView instance
 * @param {object} threats - threat perception summary
 * @returns {boolean}
 */
export function checkContextTag(contextTag, bot, view, threats) {
  if (!contextTag || contextTag === 'ANY') return true;

  const enemyUnits = threats?.enemyUnits || [];
  const ownUnits = threats?.ownUnits || [];
  const divider = bot?.battlefield?.territoryDivider ?? ((bot?.battlefield?.width || 1600) / 2);

  switch (contextTag) {
    case 'SPAWN_SWARM':
      return enemyUnits.length >= 2 || (view?.enemyValue ?? 0) >= 30;

    case 'REQ_ENEMY_GROUND_CLUSTER': {
      if (threats?.enemyGroundCount >= 3) return true;
      let groundEnemyValue = 0;
      for (let i = 0; i < enemyUnits.length; i++) {
        const u = enemyUnits[i];
        if (!u.isDead && !u.cardData?.targetsAir && !u.cardData?.isStructure) {
          groundEnemyValue += (u.cardData?.cost || 6);
          if (groundEnemyValue >= 30) return true;
        }
      }
      return false;
    }

    case 'REQ_ENEMY_AT_FRONTLINE': {
      let count = 0;
      for (let i = 0; i < enemyUnits.length; i++) {
        const u = enemyUnits[i];
        if (!u.isDead && Math.abs(u.x - divider) <= 220) {
          count++;
          if (count >= 2) return true;
        }
      }
      return false;
    }

    case 'REQ_STABLE_FRONTLINE': {
      const nearBase = threats?.enemyNearBaseCount || 0;
      const pressedBack = Boolean(threats?.isPressedBack);
      return ownUnits.length >= 2 && nearBase === 0 && !pressedBack;
    }

    case 'REQ_PUSH_ADVANTAGE': {
      const hasLead = Boolean(view?.hasTerritoryLead);
      const pushRatio = view?.pushRatio ?? 0;
      return hasLead && pushRatio >= 0.25 && ownUnits.length >= 2;
    }

    case 'REQ_ENGAGED_SWARM': {
      let count = 0;
      for (let i = 0; i < ownUnits.length; i++) {
        const u = ownUnits[i];
        if (u.isDead || u.isStructure) continue;
        const ux = u.x;
        const uy = u.y;
        let isEngaged = false;
        for (let j = 0; j < enemyUnits.length; j++) {
          const e = enemyUnits[j];
          if (!e.isDead) {
            const dx = ux - e.x;
            const dy = uy - e.y;
            if (dx * dx + dy * dy <= 62500) {
              isEngaged = true;
              break;
            }
          }
        }
        if (isEngaged) {
          count++;
          if (count >= 3) return true;
        }
      }
      return false;
    }

    default:
      return true;
  }
}

/**
 * Emergency override contract: checks whether an active pendingCombo must be aborted.
 *
 * @param {object} pendingCombo
 * @param {object} threats
 * @param {number} ownBaseHpFraction
 * @returns {boolean} true if pending combo should be cancelled
 */
export function shouldAbortPendingCombo(pendingCombo, threats, ownBaseHpFraction) {
  if (!pendingCombo) return false;

  const enemyNearBase = (threats?.enemyNearBaseCount || 0) > 0;
  const isPressedBack = Boolean(threats?.isPressedBack);

  if (pendingCombo.phase === 'HOLDING_FOR_A') {
    // Phase 1: Not yet committed to card A -> abort immediately in any crisis
    if (enemyNearBase || isPressedBack || ownBaseHpFraction < 0.5) {
      return true;
    }
    return false;
  }

  if (pendingCombo.phase === 'WAITING_FOR_B_WINDOW') {
    // Phase 2: Card A already launched -> protect B window (< 2s) unless base is critical
    if (ownBaseHpFraction < 0.15) {
      return true;
    }
    // Economy cards (like The Salvage Company) abort on mild crisis
    if (pendingCombo.isEconomyB && (enemyNearBase || isPressedBack || ownBaseHpFraction < 0.4)) {
      return true;
    }
    return false;
  }

  return false;
}

/**
 * Calculates a lightweight virtual delta state S_1 after playing cardA.
 * Never mutates the live battlefield.
 */
export function projectStateAfterCard(bot, view, threats, cardA, delaySec = 0) {
  if (!cardA) {
    return {
      ownUnitsDelta: 0,
      ownFodderCountDelta: 0,
      ownSwarmValueDelta: 0,
      frontlineSplashDamage: 0,
      expectedAttrition20s: 0,
      frontlineSurvivalRatio: 1.0
    };
  }

  const isFodder = cardA.name === 'Fodder';
  const spawnCount = isFodder ? 8 : (cardA.spawnCount || 1);
  const cost = cardA.cost || 10;

  return {
    ownUnitsDelta: spawnCount,
    ownFodderCountDelta: isFodder ? 8 : (cardA.roles?.includes?.('fusionFodder') ? 1 : 0),
    ownSwarmValueDelta: isFodder ? 48 : cost,
    frontlineSplashDamage: (cardA.splashRadius > 0 || cardA.capsuleSplashRadius > 0) ? (cardA.damage || cardA.capsuleDamage || 0) : 0,
    expectedAttrition20s: 0,
    frontlineSurvivalRatio: 1.0
  };
}

/**
 * Checks if virtual trigger nodes are currently active on the bot's team.
 */
function checkVirtualNodes(bot) {
  const active = [];

  // MODULE:capsule_sling_ready
  const ms = bot.battlefield?.moduleSystem;
  if (ms && ms.has(bot.team, 'capsule_sling')) {
    const st = ms.state?.[bot.team]?.capsule_sling;
    if (st && st.loaded >= (st.capacity || 3) - 1) {
      active.push('MODULE:capsule_sling_ready');
    }
  }

  // STATE:nuke_imminent
  if (bot.nuke) {
    if (bot.nuke.state === 'arming' || (bot.superweapons?.get?.(bot.team) > 0 && bot.nuke.canFire?.())) {
      active.push('STATE:nuke_imminent');
    }
  }

  return active;
}

/**
 * Evaluates hand card pairs and virtual trigger nodes against static and learned synergy matrices.
 *
 * @param {object} bot - BotAI instance
 * @param {Array} hand - Hand array (length 5)
 * @param {number[]} baseScores - Utility score per slot
 * @param {object} view - BoardView instance
 * @param {object} threats - threat perception summary
 * @param {number} currentPlasma - currently spendable plasma
 * @returns {object} { bestCombo, slotScoreBonuses, holdSlots }
 */
export function evaluateHandCombos(bot, hand, baseScores = [], view, threats, currentPlasma = 0) {
  const slotScoreBonuses = [0, 0, 0, 0, 0];
  const holdSlots = new Set();
  const virtualNodes = checkVirtualNodes(bot);
  const plasmaRegenRate = 2.2; // approx plasma per second

  let bestCombo = null;
  let highestScore = -Infinity;

  // Context tag result cache for the current evaluation pass
  const contextTagCache = {};

  // Pre-collect active living friendly units on battlefield for O(1) synergy check
  const activeTeamCardNames = new Set();
  const bUnits = bot.battlefield?.units;
  if (bUnits) {
    for (let ui = 0; ui < bUnits.length; ui++) {
      const u = bUnits[ui];
      if (!u.isDead && u.team === bot.team) {
        if (u.cardData?.name) activeTeamCardNames.add(u.cardData.name);
        if (u.name) activeTeamCardNames.add(u.name);
      }
    }
  }

  // Matrix lookup: iterate static pairs without array cloning, then learned pairs
  const learned = (bot.opponentMemory && bot.memoryKey && bot.profile?.imitateCombos !== false)
    ? (bot.opponentMemory.getObservedCombos?.(bot.memoryKey) || null)
    : null;
  const numStatic = AI_SYNERGY_PAIRS.length;
  const numLearned = learned ? learned.length : 0;
  const totalPairs = numStatic + numLearned;

  for (let pi = 0; pi < totalPairs; pi++) {
    let pair = null;
    if (pi < numStatic) {
      pair = AI_SYNERGY_PAIRS[pi];
    } else {
      const l = learned[pi - numStatic];
      if ((l.seen >= 2 || (l.seen >= 1 && (l.avgSwing || 0) >= 35)) && !AI_SYNERGY_PAIRS.some(p => p.cardA === l.cardA && p.cardB === l.cardB)) {
        pair = {
          cardA: l.cardA,
          cardB: l.cardB,
          optimalDelaySec: l.avgDelay || 1.2,
          synergyScore: Math.min(35, l.avgSwing || 20),
          tStat: 3.0,
          samples: l.seen,
          contextTag: 'ANY'
        };
      } else {
        continue;
      }
    }

    const tag = pair.contextTag;
    let tagPassed = contextTagCache[tag];
    if (tagPassed === undefined) {
      tagPassed = checkContextTag(tag, bot, view, threats);
      contextTagCache[tag] = tagPassed;
    }
    if (!tagPassed) {
      continue;
    }

    const isVirtualA = pair.cardA.startsWith('MODULE:') || pair.cardA.startsWith('STATE:');
    let slotA = -1;
    let slotB = -1;
    let cardA = null;
    let cardB = null;
    let isOnFieldA = false;

    if (isVirtualA) {
      if (!virtualNodes.includes(pair.cardA)) continue;
      // Find slotB in hand
      for (let j = 0; j < hand.length; j++) {
        if (hand[j] && hand[j].name === pair.cardB) {
          slotB = j;
          cardB = hand[j];
          break;
        }
      }
      if (slotB === -1) continue;
    } else {
      // Find slotA in hand
      for (let i = 0; i < hand.length; i++) {
        if (hand[i] && hand[i].name === pair.cardA) {
          slotA = i;
          cardA = hand[i];
          break;
        }
      }

      // If cardA is not in hand, check if cardA is an active living unit/structure on our battlefield
      if (slotA === -1) {
        if (activeTeamCardNames.has(pair.cardA)) {
          isOnFieldA = true;
        } else {
          continue;
        }
      }

      for (let j = 0; j < hand.length; j++) {
        if (j !== slotA && hand[j] && hand[j].name === pair.cardB) {
          slotB = j;
          cardB = hand[j];
          break;
        }
      }
      if (slotB === -1) continue;
    }

    const costA = (isVirtualA || isOnFieldA) ? 0 : (cardA?.cost || 0);
    const costB = cardB?.cost || 0;
    const totalCost = costA + costB;
    const regenDuringDelay = (isVirtualA || isOnFieldA) ? 0 : Math.floor(pair.optimalDelaySec * plasmaRegenRate);
    const netRequiredPlasma = Math.max(costA, totalCost - regenDuringDelay);

    const scoreA = slotA >= 0 ? (baseScores[slotA] || 0) : 20;
    const scoreB = slotB >= 0 ? (baseScores[slotB] || 0) : 20;
    const combinedScore = scoreA + scoreB + pair.synergyScore;

    if (combinedScore > highestScore && pair.synergyScore >= 15) {
      highestScore = combinedScore;
      bestCombo = {
        cardA: pair.cardA,
        cardB: pair.cardB,
        slotA,
        slotB,
        totalCost,
        netRequiredPlasma,
        optimalDelaySec: pair.optimalDelaySec,
        combinedScore,
        synergyBonus: pair.synergyScore,
        contextTag: pair.contextTag,
        isVirtualA: isVirtualA || isOnFieldA,
        isEconomyB: pair.cardB === 'The Salvage Company'
      };
    }
  }

  if (bestCombo) {
    if (bestCombo.slotA >= 0) {
      slotScoreBonuses[bestCombo.slotA] += bestCombo.synergyBonus * 0.8;
    }
    if (bestCombo.slotB >= 0) {
      if (bestCombo.isVirtualA) {
        slotScoreBonuses[bestCombo.slotB] += bestCombo.synergyBonus * 0.8;
      } else {
        // Hold card B until card A has been deployed
        holdSlots.add(bestCombo.slotB);
      }
    }
  }

  return { bestCombo, slotScoreBonuses, holdSlots };
}
